import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import type { SetupState } from "@dentacore/shared-types";

import { readSetupState } from "../api/setup";
import styles from "./setup.module.css";
import { useCan } from "../auth/use-can";

/**
 * The line on Home that carries an admin back to guided setup (decision G3).
 *
 * **It draws nothing at all unless there is something to say.** Not an admin,
 * still loading, the call failed, or the clinic is ready — all four render
 * null, so Home is untouched for everyone whose clinic is already working.
 * That is the whole reason the banner needs no dismissal (G4): it removes
 * itself when the four steps are met, and the honest way to remove it early is
 * to finish them.
 *
 * **A failure here is silent.** This is a prompt about setup, not part of the
 * dashboard; a clinic that can already see today's list should not be told its
 * setup check broke.
 */
export function SetupBanner(): React.JSX.Element | null {
  const can = useCan();
  const location = useLocation();
  const [state, setState] = useState<SetupState | null>(null);

  const isAdmin = can("settings.clinic.manage");
  const ready = state?.ready === true;

  /**
   * **Re-read on every navigation**, which is what makes it live rather than a
   * snapshot from whenever the shell mounted. The steps deep-link into Settings
   * and come back; opening a location has to make the count move, or the banner
   * is telling a stale story about work the reader has just finished.
   *
   * The endpoint counts four rows and stores nothing (G4), so this is cheap and
   * cannot disagree with the clinic's actual data.
   */
  useEffect(() => {
    if (!isAdmin) return;

    const controller = new AbortController();

    const read = (): void => {
      readSetupState(controller.signal)
        .then(setState)
        .catch(() => {
          /* Silent by design — see above. */
        });
    };

    read();

    /**
     * **And every twenty seconds while there is still work to do.**
     *
     * Navigation alone is not enough: opening a location, adding a chair and
     * entering a price all happen *without leaving Settings*, so a
     * navigation-only refresh leaves the banner insisting on work the reader
     * has just finished — which is worse than no banner, because it is wrong.
     *
     * **It stops the moment the clinic is ready.** `ready` is in this effect's
     * dependencies, so the last poll that finds four of four also tears the
     * interval down — this costs a clinic exactly as long as its onboarding and
     * nothing after, and the endpoint counts four rows and stores nothing (G4).
     * Polling was chosen over a change notification because the alternative is
     * every write path in Settings remembering to fire one, and the one that
     * forgets is the bug this is fixing.
     */
    if (ready) return () => controller.abort();

    const timer = window.setInterval(read, 20_000);

    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [isAdmin, location.pathname, ready]);

  // Not on `/setup` itself: a banner pointing at the page you are reading is
  // noise, and the page already says everything the banner would.
  if (location.pathname === "/setup") return null;
  if (!isAdmin || state === null || state.ready) return null;

  const outstanding = state.steps.filter((step) => step.required && !step.done);

  return (
    <div className={styles.banner} data-testid="setup-banner">
      <div className={styles.bannerBody}>
        <p className={styles.bannerTitle}>
          Setup: {state.requiredStepsDone} of {state.requiredStepsTotal} done
        </p>
        <p className={styles.bannerNote}>
          {outstanding.length === 0
            ? "No single location has both a chair and a procedure yet, so there is nowhere to book."
            : `Still to do: ${outstanding.map((step) => LABELS[step.key] ?? step.key).join(", ")}.`}
        </p>
      </div>
      <Link to="/setup" className={styles.bannerLink} data-testid="setup-banner-link">
        Finish setting up
      </Link>
    </div>
  );
}

/**
 * Short forms, for a line that has to fit on one row. The full sentence for
 * each step lives on `/setup` itself, which is where the work happens.
 */
const LABELS: Record<string, string> = {
  locations: "a location",
  chairs: "a chair",
  providers: "a dentist or hygienist",
  procedures: "your procedures",
};
